import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles } from './iconMap';
import { useSkin } from '../hooks/useSkin';
import { useTheme } from '../hooks/useTheme';
import { CLASSIC_OPTION, PREMIUM_THEMES } from '../premiumThemes';
import './theme-picker.css';

const OPTIONS = [CLASSIC_OPTION, ...PREMIUM_THEMES];

/**
 * Flowing colour reveal — floods the new theme's gradient OUT FROM the clicked
 * swatch (origin x/y), expanding to cover the viewport. The actual theme is NOT
 * applied on click; instead it's applied via `onCovered()` once the opaque flood
 * has fully covered the screen, then the flood dissolves to REVEAL the freshly
 * themed UI underneath. That sequencing is what makes the change ride in with the
 * wash instead of snapping instantly. Because the picker sits on the left edge,
 * the circle sweeps diagonally across to the opposite corner — a "water flow".
 * Pure DOM overlay (no View Transitions dependency).
 *
 * @param {string}   gradient   CSS background for the flood layer
 * @param {number}   ox         origin x (viewport px) — defaults to screen centre
 * @param {number}   oy         origin y (viewport px)
 * @param {function} onCovered  apply the theme here (runs while hidden under the flood)
 */
const EXPAND_MS = 720;   // flood sweeps out from the swatch
const SETTLE_MS = 110;   // beat to let the swapped theme paint under the cover
const FADE_MS = 520;     // flood dissolves to reveal the new theme

function rippleReveal(gradient, ox, oy, onCovered) {
    const reduce = typeof window !== 'undefined' && window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (typeof document === 'undefined' || reduce) { if (onCovered) onCovered(); return; }

    const w = window.innerWidth || 1280;
    const h = window.innerHeight || 800;
    const x = Number.isFinite(ox) ? ox : w / 2;
    const y = Number.isFinite(oy) ? oy : h / 2;
    // Radius that reaches the farthest viewport corner from the origin, so the
    // flood always fully covers the screen no matter where it starts.
    const radius = Math.hypot(Math.max(x, w - x), Math.max(y, h - y)) + 4;

    const el = document.createElement('div');
    el.setAttribute('aria-hidden', 'true');
    el.style.cssText = [
        'position:fixed', 'inset:0', 'z-index:99999', 'pointer-events:none',
        `background:${gradient}`,
        `clip-path:circle(0px at ${x}px ${y}px)`,
        `-webkit-clip-path:circle(0px at ${x}px ${y}px)`,
        'will-change:clip-path,opacity', 'opacity:1',
        'filter:saturate(1.12)',
    ].join(';');
    document.body.appendChild(el);
    // Force reflow so the initial 0px radius is committed before we animate.
    void el.getBoundingClientRect();
    el.style.transition = `clip-path ${EXPAND_MS}ms cubic-bezier(0.22,1,0.36,1), -webkit-clip-path ${EXPAND_MS}ms cubic-bezier(0.22,1,0.36,1)`;
    el.style.clipPath = `circle(${radius}px at ${x}px ${y}px)`;
    el.style.webkitClipPath = `circle(${radius}px at ${x}px ${y}px)`;

    // Once the flood fully covers the screen, swap the theme underneath it…
    window.setTimeout(() => {
        if (onCovered) onCovered();
        // …let it paint, then dissolve the flood to reveal the new theme.
        window.setTimeout(() => {
            el.style.transition = `opacity ${FADE_MS}ms ease`;
            el.style.opacity = '0';
            window.setTimeout(() => el.remove(), FADE_MS + 30);
        }, SETTLE_MS);
    }, EXPAND_MS);
}

/**
 * Apply a skin + dark/light mode to the DOM SYNCHRONOUSLY — mirrors the work
 * Layout.applyPremium / useSkin.applySkin / useTheme.applyTheme do via React +
 * events. Needed so the change lands inside a View Transition snapshot (React's
 * async effects would miss the capture window). Also fires `v3-skin-change` so
 * palette-aware charts recolour.
 */
function applyThemeDOM(id, mode) {
    try {
        document.documentElement.setAttribute('data-skin', id);
        const on = id !== 'classic';
        document.body.classList.toggle('v3-premium', on);
        if (on) document.body.setAttribute('data-pm-theme', id);
        else document.body.removeAttribute('data-pm-theme');
        if (mode === 'dark' || mode === 'light') {
            document.documentElement.setAttribute('data-theme', mode);
            const dark = mode === 'dark';
            document.body.classList.toggle('dark-only', dark);
            document.body.classList.toggle('light-only', !dark);
        }
        window.dispatchEvent(new CustomEvent('v3-skin-change', { detail: id }));
    } catch (_) { /* noop */ }
}

/**
 * Genuine wavefront reveal via the View Transitions API: snapshot the OLD themed
 * UI, apply the NEW theme, then wipe an expanding circle from the clicked swatch
 * so old and new are split by the moving edge. Falls back to the flood overlay
 * when View Transitions are unavailable (e.g. Safari) or reduced-motion is on.
 *
 * @returns {boolean} true if the View Transition path ran (caller skips fallback)
 */
function flowReveal(opt, ox, oy, applyReact) {
    const reduce = typeof window !== 'undefined' && window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const supported = typeof document !== 'undefined' && typeof document.startViewTransition === 'function';
    if (!supported || reduce) return false;

    const w = window.innerWidth || 1280;
    const h = window.innerHeight || 800;
    const x = Number.isFinite(ox) ? ox : w / 2;
    const y = Number.isFinite(oy) ? oy : h / 2;
    const radius = Math.hypot(Math.max(x, w - x), Math.max(y, h - y)) + 4;

    const vt = document.startViewTransition(() => {
        applyThemeDOM(opt.id, opt.mode);
        applyReact();
    });
    vt.ready.then(() => {
        document.documentElement.animate(
            {
                clipPath: [
                    `circle(0px at ${x}px ${y}px)`,
                    `circle(${radius}px at ${x}px ${y}px)`,
                ],
            },
            {
                duration: EXPAND_MS,
                easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
                pseudoElement: '::view-transition-new(root)',
            },
        );
    }).catch(() => { /* a concurrent transition was skipped — fine */ });
    return true;
}

/**
 * Appearance picker — opens a glass popover from the sidebar account bar with a
 * swatch gallery (Classic + 5 premium themes). Selecting one applies the skin
 * live (via useSkin) and flips the matching dark/light base (via useTheme) so
 * text + legacy pages stay readable. Original "Classic" is always first.
 */
export default function ThemePicker() {
    const { t } = useTranslation();
    const { skin, setSkin } = useSkin();
    const { setTheme } = useTheme();
    const [open, setOpen] = useState(false);
    const triggerRef = useRef(null);
    const popRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            // The popover is portaled to <body>, so check BOTH the trigger and
            // the popover before treating a click as "outside".
            if (triggerRef.current && triggerRef.current.contains(e.target)) return;
            if (popRef.current && popRef.current.contains(e.target)) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [open]);

    const apply = useCallback((opt, e) => {
        // Flood the new theme's colour out from the clicked swatch. The skin is
        // applied by onCovered (once the flood hides the screen), so the change is
        // revealed as the wash dissolves rather than snapping instantly on click.
        let ox, oy;
        try {
            const sw = e && e.currentTarget && e.currentTarget.querySelector('.v3-tp-swatch');
            const r = (sw || (e && e.currentTarget)).getBoundingClientRect();
            ox = r.left + r.width / 2;
            oy = r.top + r.height / 2;
        } catch (_) { /* fall back to centre */ }

        const applyReact = () => {
            setSkin(opt.id);
            if (opt.mode) setTheme(opt.mode);
        };
        // Preferred: true wavefront reveal (old/new split by the moving edge).
        // Fallback: flood overlay that hides the swap, then dissolves to reveal.
        if (!flowReveal(opt, ox, oy, applyReact)) {
            rippleReveal(opt.swatch, ox, oy, applyReact);
        }
        setOpen(false);
    }, [setSkin, setTheme]);

    return (
        <div className="v3-theme-picker">
            <button
                ref={triggerRef}
                className={`v3-account-btn v3-skin-toggle${skin !== 'classic' ? ' is-active' : ''}`}
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={t('Choose appearance theme')}
                data-tooltip={t('Themes')}
            >
                <Sparkles size={16} />
            </button>

            {/* Portaled to <body> so it escapes the sidebar's backdrop-filter
                (which would otherwise make position:fixed resolve against the
                sidebar) and its overflow:hidden clip. */}
            {open && createPortal(
                <div className="v3-tp-popover" role="menu" ref={popRef}>
                    <div className="v3-tp-title">{t('Appearance')}</div>
                    <div className="v3-tp-sub-h">{t('Pick a theme — Spatial Brief stays Classic')}</div>
                    <div className="v3-tp-list">
                        {OPTIONS.map((opt) => {
                            const active = skin === opt.id;
                            return (
                                <button
                                    key={opt.id}
                                    role="menuitemradio"
                                    aria-checked={active}
                                    className={`v3-tp-opt${active ? ' active' : ''}`}
                                    onClick={(e) => apply(opt, e)}
                                >
                                    <span className="v3-tp-swatch" style={{ background: opt.swatch }}>
                                        {active && (
                                            <svg viewBox="0 0 24 24" width="12" height="12" fill="none"
                                                stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M20 6 9 17l-5-5" />
                                            </svg>
                                        )}
                                    </span>
                                    <span className="v3-tp-meta">
                                        <span className="v3-tp-name">{opt.name}</span>
                                        <span className="v3-tp-sub">{opt.sub}</span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
}
